import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/store/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login.vue'),
    meta: { layout: 'blank', title: 'nav.login' },
  },
  {
    path: '/',
    redirect: { name: 'dashboard' },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/Dashboard.vue'),
    // Arco Pro's workplace has no page heading and paints its own panels.
    meta: { title: 'nav.dashboard', bare: true },
  },
  {
    path: '/nodes',
    name: 'nodes',
    component: () => import('@/views/Nodes.vue'),
    meta: { title: 'nav.nodes' },
  },
  {
    path: '/nodes/:id',
    name: 'node-detail',
    component: () => import('@/views/NodeDetail.vue'),
    meta: { title: 'nav.nodes', bare: true },
  },
  {
    path: '/logs',
    name: 'access-logs',
    component: () => import('@/views/AccessLogs.vue'),
    meta: { title: 'nav.accessLogs', ownSurface: true },
  },
  {
    path: '/security',
    name: 'security-events',
    component: () => import('@/views/SecurityEvents.vue'),
    meta: { title: 'nav.securityEvents', ownSurface: true },
  },
  {
    path: '/analytics',
    name: 'analytics',
    component: () => import('@/views/Analytics.vue'),
    meta: { title: 'nav.analytics', ownSurface: true },
  },
  {
    path: '/analytics/access',
    name: 'access-stats',
    component: () => import('@/views/AccessStats.vue'),
    meta: { title: 'nav.accessStats', ownSurface: true },
  },
  {
    path: '/analytics/origin',
    name: 'origin-stats',
    component: () => import('@/views/OriginStats.vue'),
    meta: { title: 'nav.originStats', ownSurface: true },
  },
  {
    path: '/tasks',
    name: 'tasks',
    component: () => import('@/views/Tasks.vue'),
    meta: { title: 'nav.tasks' },
  },
  {
    path: '/line-groups',
    name: 'line-groups',
    component: () => import('@/views/LineGroups.vue'),
    meta: { title: 'nav.lineGroups', ownSurface: true },
  },
  {
    path: '/line-groups/:id/dns',
    name: 'line-group-dns',
    component: () => import('@/views/LineGroupDns.vue'),
    meta: { title: 'nav.lineGroups', bare: true },
  },
  {
    path: '/cname-zones',
    name: 'cname-zones',
    component: () => import('@/views/CnameZones.vue'),
    meta: { title: 'nav.cnameZones' },
  },
  {
    path: '/dns-providers',
    name: 'dns-providers',
    component: () => import('@/views/DnsProviders.vue'),
    meta: { title: 'nav.dnsProviders' },
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/views/Users.vue'),
    meta: { title: 'nav.users' },
  },
  {
    path: '/regions',
    name: 'regions',
    component: () => import('@/views/Regions.vue'),
    meta: { title: 'nav.regions' },
  },
  {
    path: '/user-groups',
    name: 'user-groups',
    component: () => import('@/views/UserGroups.vue'),
    meta: { title: 'nav.userGroups' },
  },
  {
    path: '/realname',
    name: 'realname-settings',
    component: () => import('@/views/RealnameSettings.vue'),
    meta: { title: 'realnameSettings.title', ownSurface: true },
  },
  // 会员等级: the VIP badge and 工单优先, 经验 and the curve of 等级.
  {
    path: '/member-settings',
    name: 'member-settings',
    component: () => import('@/views/MemberSettings.vue'),
    meta: { title: 'memberSettings.title', ownSurface: true },
  },
  {
    path: '/packages',
    name: 'packages',
    component: () => import('@/views/Packages.vue'),
    meta: { title: 'packages.title' },
  },
  {
    path: '/topups',
    name: 'topups',
    component: () => import('@/views/TopUps.vue'),
    meta: { title: 'topups.title', ownSurface: true },
  },
  {
    path: '/payment',
    name: 'payment',
    component: () => import('@/views/Payment.vue'),
    meta: { title: 'payment.title', ownSurface: true },
  },
  // 财务管理's own pages; 充值订单 and 支付配置 keep their addresses.
  {
    path: '/finance/orders',
    name: 'finance-orders',
    component: () => import('@/views/FinanceOrders.vue'),
    meta: { title: 'finOrders.title', ownSurface: true },
  },
  {
    path: '/finance/remittances',
    name: 'finance-remittances',
    component: () => import('@/views/FinanceRemittances.vue'),
    meta: { title: 'remits.title', ownSurface: true },
  },
  {
    path: '/finance/withdrawals',
    name: 'finance-withdrawals',
    component: () => import('@/views/FinanceWithdrawals.vue'),
    meta: { title: 'withdrawals.title', ownSurface: true },
  },
  {
    path: '/finance/invoices',
    name: 'finance-invoices',
    component: () => import('@/views/FinanceInvoices.vue'),
    meta: { title: 'invoices.title', ownSurface: true },
  },
  {
    path: '/finance/refunds',
    name: 'finance-refunds',
    component: () => import('@/views/FinanceRefunds.vue'),
    meta: { title: 'refunds.title', ownSurface: true },
  },
  {
    path: '/finance/settings',
    name: 'finance-settings',
    component: () => import('@/views/FinanceSettings.vue'),
    meta: { title: 'finSettings.title', ownSurface: true },
  },
  // 工单管理. A ticket's id is digits, so /tickets/templates is never one.
  // 消息中心 and 消息接收配置: the bell's 查看更多 and its settings.
  {
    path: '/messages',
    name: 'messages',
    component: () => import('@/views/MessageCenter.vue'),
    meta: { title: 'inbox.title', ownSurface: true },
  },
  {
    path: '/messages/settings',
    name: 'message-settings',
    component: () => import('@/views/MessageSettings.vue'),
    meta: { title: 'inbox.settingsTitle', ownSurface: true },
  },
  {
    path: '/tickets',
    name: 'tickets',
    component: () => import('@/views/Tickets.vue'),
    meta: { title: 'tickets.title', ownSurface: true },
  },
  {
    path: '/tickets/:id(\\d+)',
    name: 'ticket-detail',
    component: () => import('@/views/TicketDetail.vue'),
    // A heading of its own: the ticket's title, state and facts.
    meta: { title: 'tickets.detailTitle', bare: true },
  },
  // 应用加速.
  {
    path: '/l4-rules',
    name: 'l4-rules',
    component: () => import('@/views/L4Rules.vue'),
    meta: { title: 'l4.title', ownSurface: true },
  },
  {
    path: '/complaints',
    name: 'complaints',
    component: () => import('@/views/Complaints.vue'),
    meta: { title: 'complaints.title', ownSurface: true },
  },
  {
    path: '/tickets/templates',
    name: 'ticket-templates',
    component: () => import('@/views/TicketTemplates.vue'),
    meta: { title: 'ticketTemplates.title', ownSurface: true },
  },
  {
    path: '/tickets/settings',
    name: 'ticket-settings',
    component: () => import('@/views/TicketSettings.vue'),
    meta: { title: 'ticketSettings.title', ownSurface: true },
  },
  {
    path: '/mail',
    name: 'mail-settings',
    component: () => import('@/views/MailSettings.vue'),
    meta: { title: 'mail.title', ownSurface: true },
  },
  {
    path: '/captcha',
    name: 'captcha-settings',
    component: () => import('@/views/CaptchaSettings.vue'),
    meta: { title: 'captchaSettings.title', ownSurface: true },
  },
  {
    path: '/sms',
    name: 'sms-settings',
    component: () => import('@/views/SmsSettings.vue'),
    meta: { title: 'smsSettings.title', ownSurface: true },
  },
  {
    path: '/message-templates',
    name: 'message-templates',
    component: () => import('@/views/MessageTemplates.vue'),
    meta: { title: 'messageTemplates.title', ownSurface: true },
  },
  {
    path: '/assistant',
    name: 'assistant-settings',
    component: () => import('@/views/AssistantSettings.vue'),
    meta: { title: 'assistantSettings.title', ownSurface: true },
  },
  {
    path: '/audit',
    name: 'audit',
    component: () => import('@/views/Audit.vue'),
    meta: { title: 'audit.title' },
  },
  {
    path: '/ip-lists',
    name: 'ip-lists',
    component: () => import('@/views/IPLists.vue'),
    meta: { title: 'ipLists.title', ownSurface: true },
  },
  {
    path: '/firewall',
    name: 'firewall',
    component: () => import('@/views/FirewallSettings.vue'),
    meta: { title: 'firewall.title' },
  },
  {
    path: '/ban-logs',
    name: 'ban-logs',
    component: () => import('@/views/BanLogs.vue'),
    meta: { title: 'banLogs.title' },
  },
  {
    path: '/system-settings',
    name: 'system-settings',
    component: () => import('@/views/SystemSettings.vue'),
    meta: { title: 'systemSettings.title', ownSurface: true },
  },
  {
    path: '/subscriptions',
    name: 'subscriptions',
    component: () => import('@/views/Subscriptions.vue'),
    meta: { title: 'subscriptions.title' },
  },
  {
    path: '/console-domains',
    name: 'console-domains',
    component: () => import('@/views/ConsoleDomains.vue'),
    meta: { title: 'consoleDomains.title' },
  },
  {
    path: '/control-tls',
    name: 'control-tls',
    component: () => import('@/views/ControlTLS.vue'),
    meta: { title: 'controlTLS.title' },
  },
  {
    path: '/clickhouse',
    name: 'clickhouse',
    component: () => import('@/views/ClickHouse.vue'),
    meta: { title: 'clickhouse.title', ownSurface: true },
  },
  {
    path: '/default-pages',
    name: 'default-pages',
    component: () => import('@/views/DefaultPages.vue'),
    meta: { title: 'defaultPages.title' },
  },
  {
    path: '/domains',
    name: 'domains',
    component: () => import('@/views/Domains.vue'),
    meta: { title: 'nav.domains', ownSurface: true },
  },
  {
    path: '/domains/:id',
    name: 'domain-detail',
    component: () => import('@/views/DomainDetail.vue'),
    meta: { title: 'domains.detailTitle', bare: true },
  },
  {
    path: '/domains/:id/rules',
    name: 'rules',
    component: () => import('@/views/Rules.vue'),
    meta: { title: 'rules.title', bare: true },
  },
  {
    path: '/certificates',
    name: 'certificates',
    component: () => import('@/views/Certificates.vue'),
    meta: { title: 'certificates.title', ownSurface: true },
  },
  // 系统更新: the release site checked, the control plane and nodes updated.
  {
    path: '/system-update',
    name: 'system-update',
    component: () => import('@/views/SystemUpdate.vue'),
    meta: { title: 'systemUpdate.title', ownSurface: true },
  },
  {
    path: '/license',
    name: 'license',
    component: () => import('@/views/License.vue'),
    meta: { title: 'license.title', ownSurface: true },
  },
  // 安全中心: the signed-in account's own password, MFA device, sessions
  // and sign-ins.
  {
    path: '/account/security',
    name: 'account-security',
    component: () => import('@/views/SecurityCenter.vue'),
    meta: { title: 'securityCenter.title', ownSurface: true },
  },
  { path: '/:pathMatch(.*)*', redirect: { name: 'dashboard' } },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  if (to.name === 'login') return true
  if (!auth.isLoggedIn) return { name: 'login', query: { next: to.fullPath } }
  if (!auth.user) await auth.refreshMe()
  // Still signed in with no account: /me did not answer. Fail the
  // navigation rather than show the console without its account; the first
  // one failing puts a reload on the boot screen (bootFailed).
  if (auth.isLoggedIn && !auth.user) throw new Error('the account could not be loaded')
  return true
})

export default router
